// src/components/SuccessPage.jsx

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Container, Typography, Box, Button } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import Confetti from 'react-confetti'; // For celebratory effect
import { CheckCircle } from 'lucide-react'; // Success icon

// Styled Components
const CustomButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0, #a855f7)',
  color: '#fff',
  padding: theme.spacing(1, 3),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2a5bb5, #7b3fb8)',
    boxShadow: '0 8px 30px rgba(59, 130, 246, 0.5)',
  },
}));

const SuccessContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  background: 'linear-gradient(135deg, rgba(59, 44, 91, 0.8), rgba(31, 41, 55, 0.9))',
  border: '1px solid rgba(168, 85, 247, 0.3)',
  maxWidth: '600px',
  margin: '0 auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  textAlign: 'center',
}));

const SuccessPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const theme = useTheme();

  // Redirect if no payment success state
  useEffect(() => {
    if (!state?.paymentSuccess) {
      navigate('/wizard');
    }
  }, [navigate, state]);

  const handleContinue = () => {
    navigate('/wizard'); // Redirect to wizard or another page
  };

  if (!state?.paymentSuccess) return null; // Render nothing until redirect

  const planName = state.planName || 'Your Plan';

  return (
    <motion.div
      className="bg-gradient-to-b from-gray-900 to-purple-900 text-gray-300 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={200} />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
              mb: 4,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Payment Successful
          </Typography>
          <SuccessContainer>
            <Box sx={{ mb: 3 }}>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
            </Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
              Congratulations!
            </Typography>
            <Typography variant="body1" sx={{ color: '#e0e0e0', mb: 3 }}>
              Your payment for the <strong>{planName}</strong> plan has been successfully processed. As a valued customer on a paid plan, a dedicated lead has been assigned to assist you with the customization of your chat or phone bot. They will reach out to you soon to discuss your requirements and ensure a seamless setup.
            </Typography>
            <CustomButton onClick={handleContinue}>
              Continue to Dashboard
            </CustomButton>
          </SuccessContainer>
        </motion.div>
      </Container>
    </motion.div>
  );
};

export default SuccessPage;
